import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/shared/ui/components/shadcn/dialog';
import { Button } from '@/shared/ui/components/shadcn/button';
import { SegmentedControl, Text, TextInput } from '@/shared/ui';
import { VistoryService } from '../services/vistory-service';
import { deriveRoomsFromProperty } from '../utils/vistory-rooms';
import type { VistoryType } from '../types/vistory.types';
import type { PropertyNestedResponse } from '@services/housien/types-property.d';

interface Props {
  agencyId: string;
  leaseId: string;
  /** Required by the create route, which reads body["property_id"]. */
  propertyId: string;
  /** The full record, when loaded — only rooms derivation needs it. */
  property?: PropertyNestedResponse | null;
  onClose: () => void;
  onCreated: () => void;
}

/**
 * Modal form to create a vistory with property-first approach.
 * Captures title and type (move_in / move_out), then auto-derives rooms.
 */
export function CreateVistoryModal({
  agencyId, leaseId, propertyId, property, onClose, onCreated,
}: Props) {
  const { t } = useTranslation('features/vistories');
  const [title, setTitle] = useState('');
  const [type, setType] = useState<VistoryType>('move_in');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const typeOptions = [
    { label: t('vistory.type_move_in'), value: 'move_in' as const },
    { label: t('vistory.type_move_out'), value: 'move_out' as const },
  ];

  const canSubmit = title.trim().length > 0;

  async function handleSubmit() {
    if (!canSubmit) return;
    setSubmitting(true);
    setError('');
    try {
      const created = await VistoryService.createVistory(agencyId, leaseId, {
        property_id: propertyId,
        title: title.trim(),
        type,
      });
      if (property) {
        const rooms = deriveRoomsFromProperty(property);
        await VistoryService.updateVistory(agencyId, created.id, { rooms });
      }
      onCreated();
      onClose();
    } catch {
      setError(t('vistory.create_error'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('vistory.create_title')}</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-[var(--space-4)]">
          {property && (
            <div className="rounded-(--radius-md) border border-[var(--border-subtle)] bg-[var(--surface-secondary)] p-[var(--space-3)]">
              <Text size="xs" fw={500} className="text-[var(--text-secondary)]">
                {t('vistory.property_label')}
              </Text>
              <Text size="sm" className="text-[var(--text-primary)]">
                {property.description?.headline ?? property.location?.street ?? '—'}
              </Text>
            </div>
          )}
          <TextInput
            label={t('vistory.field_title')}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={t('vistory.title_placeholder')}
          />
          <div className="flex flex-col gap-[var(--space-1)]">
            <Text span size="xs" fw={500} className="text-[var(--text-secondary)]">
              {t('vistory.type_label')}
            </Text>
            <SegmentedControl
              items={typeOptions}
              value={type}
              onChange={(v) => setType(v as VistoryType)}
            />
          </div>
          {error && (
            <Text span size="xs" className="text-[var(--status-error)]">
              {error}
            </Text>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting}>
            {t('vistory.cancel')}
          </Button>
          <Button
            data-testid="create-vistory-submit"
            onClick={handleSubmit}
            disabled={!canSubmit || submitting}
          >
            {t('vistory.create_submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
